/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { ModalShell } from '../../../shared/modals/ModalShell';
import type { PickupCollectMethod } from '../../domain/orderCompletePayment';
import { formatMoney } from '../../../shared/format/public-api';

type Props = {
  visible: boolean;
  amount: number;
  showMercadoPagoQr: boolean;
  sending?: boolean;
  onClose: () => void;
  onSelect: (method: PickupCollectMethod) => void;
};

const OPTIONS: { key: PickupCollectMethod; label: string; hint: string; icon: keyof typeof Ionicons.glyphMap }[] = [
  { key: 'cash', label: 'Efectivo', hint: 'Lo cobrás en el mostrador', icon: 'cash-outline' },
  { key: 'transfer', label: 'Transferencia', hint: 'Te depositan y lo anotás', icon: 'swap-horizontal-outline' },
  { key: 'mercadopago_qr', label: 'Mercado Pago QR', hint: 'El cliente escanea y paga', icon: 'qr-code-outline' },
];

export function OrderCollectPaymentSheet({
  visible,
  amount,
  showMercadoPagoQr,
  sending = false,
  onClose,
  onSelect,
}: Props) {
  return (
    <ModalShell visible={visible} onClose={sending ? () => undefined : onClose} title="Cómo lo cobrás">
      <Text style={styles.amount}>{formatMoney(amount)}</Text>
      <Text style={styles.hint}>Elegí el medio. Recién ahí se marca como entregado.</Text>
      <View style={styles.list}>
        {OPTIONS.filter((opt) => opt.key !== 'mercadopago_qr' || showMercadoPagoQr).map((opt) => (
          <TouchableOpacity
            key={opt.key}
            style={[styles.row, sending && styles.rowDisabled]}
            onPress={() => onSelect(opt.key)}
            disabled={sending}
            testID={`orders.collect.method.${opt.key}`}
          >
            <Ionicons name={opt.icon} size={22} color={ticketColors.gold} />
            <View style={styles.rowText}>
              <Text style={styles.rowLabel}>{opt.label}</Text>
              <Text style={styles.rowHint}>{opt.hint}</Text>
            </View>
            <Ionicons name="chevron-forward" size={16} color={ticketColors.ink3} />
          </TouchableOpacity>
        ))}
      </View>
    </ModalShell>
  );
}

const styles = StyleSheet.create({
  amount: {
    fontFamily: ticketFonts.sans,
    fontSize: 22,
    fontWeight: '800',
    color: ticketColors.ink,
    marginBottom: 6,
  },
  hint: {
    fontFamily: ticketFonts.sans,
    fontSize: 13,
    color: ticketColors.ink2,
    marginBottom: 16,
  },
  list: { gap: 8 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 14,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  rowDisabled: { opacity: 0.5 },
  rowText: { flex: 1 },
  rowLabel: {
    fontFamily: ticketFonts.sans,
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  rowHint: {
    fontFamily: ticketFonts.sans,
    fontSize: 12,
    color: ticketColors.ink2,
    marginTop: 2,
  },
});
